import React, { useState } from "react";
import { IconTrash, IconPlus, IconFilter, IconDatabase } from "@tabler/icons";
import {
  TextInput,
  Select,
  Button,
  Paper,
  Group,
  Stack,
  Divider,
  MultiSelect,
  Box,
  Accordion,
  Text,
  ActionIcon,
  Collapse,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import _ from "lodash";
import moment from "moment";

// Status options for filters
const ORDER_STATUSES = [
  { value: "INF", label: "In Force" },
  { value: "LAP", label: "Lapsed" },
  { value: "PEN", label: "Pending" },
  { value: "CAN", label: "Cancelled" },
];

const CLIENT_STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const PRODUCT_CATEGORIES = [
  { value: "LIFE", label: "Life" },
  { value: "MPF", label: "MPF" },
  { value: "GI", label: "GI" },
];

// Collection options
const COLLECTIONS = [
  {
    value: "orders",
    label: "Orders",
    filters: [
      {
        field: "status",
        label: "Status",
        type: "select",
        options: ORDER_STATUSES,
      },
      {
        field: "productCategory",
        label: "Product Category",
        type: "select",
        options: PRODUCT_CATEGORIES,
      },
      {
        field: "inforceDate",
        label: "Inforce Date",
        type: "date",
      },
      {
        field: "submissionDate",
        label: "Submission Date",
        type: "date",
      },
      {
        field: "createdAt",
        label: "Creation At",
        type: "date",
      },
    ],
  },
  {
    value: "clients",
    label: "Clients",
    filters: [
      {
        field: "status",
        label: "Status",
        type: "select",
        options: CLIENT_STATUSES,
      },
      {
        field: "createdAt",
        label: "Creation At",
        type: "date",
      },
    ],
  },
];

const DataSourceForm = ({ dataSource, onUpdate, index }) => {
  const [showFilters, setShowFilters] = useState(false);

  const updateFilter = (field, value) => {
    const updatedDataSource = {
      ...dataSource,
      filters: {
        ...dataSource.filters,
        [field]: value,
      },
    };
    onUpdate(index, updatedDataSource);
  };

  const updateDataSourceField = (field, value) => {
    const updatedDataSource = { ...dataSource, [field]: value };
    onUpdate(index, updatedDataSource);
  };

  // Get available filters for the selected collection
  const getAvailableFilters = () => {
    const selectedCollection = COLLECTIONS.find(
      (col) => col.value === dataSource.collectionName
    );
    return selectedCollection?.filters || [];
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    const filters = dataSource.filters || {};
    const availableFilters = getAvailableFilters();

    return availableFilters.some((filterDef) => {
      const filterValue = filters[filterDef.field];
      if (filterDef.type === "select") {
        return filterValue && filterValue.length > 0;
      } else if (filterDef.type === "date") {
        return filterValue?.gte || filterValue?.lte;
      }
      return false;
    });
  };

  // Render filter field based on type
  const renderFilterField = (filterDef) => {
    const filterValue = dataSource.filters?.[filterDef.field];

    if (filterDef.type === "select") {
      return (
        <MultiSelect
          key={filterDef.field}
          label={filterDef.label}
          value={filterValue || []}
          onChange={(value) => updateFilter(filterDef.field, value)}
          data={filterDef.options}
          placeholder={`Select ${filterDef.label.toLowerCase()}`}
          size="xs"
        />
      );
    } else if (filterDef.type === "date") {
      return (
        <Group key={filterDef.field} grow>
          <DatePicker
            label={`${filterDef.label} (From)`}
            value={filterValue?.gte ? moment(filterValue.gte).toDate() : null}
            onChange={(date) => {
              const currentFilter = filterValue || {};
              updateFilter(filterDef.field, {
                ...currentFilter,
                gte: date,
              });
            }}
            placeholder="YYYY-MM-DD"
            allowFreeInput
            clearable
            size="xs"
          />
          <DatePicker
            label={`${filterDef.label} (To)`}
            value={filterValue?.lte ? moment(filterValue.lte).toDate() : null}
            onChange={(date) => {
              const currentFilter = filterValue || {};
              updateFilter(filterDef.field, {
                ...currentFilter,
                lte: date,
              });
            }}
            placeholder="YYYY-MM-DD"
            allowFreeInput
            clearable
            size="xs"
          />
        </Group>
      );
    }
    return null;
  };

  return (
    <Stack spacing="sm">
      <Group>
        <TextInput
          label="Data Source Name"
          value={dataSource.name || ""}
          onChange={(e) => updateDataSourceField("name", e.target.value)}
          placeholder="e.g., activeOrders"
          required
          size="xs"
          autoComplete="off"
        />

        <Select
          label="Collection"
          value={dataSource.collectionName || ""}
          onChange={(value) => updateDataSourceField("collectionName", value)}
          data={COLLECTIONS}
          required
          size="xs"
        />

        <Box>
          <Text size="xs" color="dimmed" mb={4}>
            Filters
          </Text>
          <ActionIcon
            variant={showFilters ? "filled" : "outline"}
            color={hasActiveFilters() ? "blue" : "gray"}
            onClick={() => setShowFilters(!showFilters)}
            size="md"
          >
            <IconFilter size={16} />
          </ActionIcon>
        </Box>
      </Group>

      <Collapse in={showFilters}>
        <Stack spacing="sm" mt="sm">
          <Divider label="Filter Options" />

          {!dataSource.collectionName && (
            <Text size="sm" color="dimmed" style={{ fontStyle: "italic" }}>
              Please select a collection first to see available filters
            </Text>
          )}

          {dataSource.collectionName && getAvailableFilters().length === 0 && (
            <Text size="sm" color="dimmed" style={{ fontStyle: "italic" }}>
              No filters available for this collection
            </Text>
          )}

          {dataSource.collectionName &&
            getAvailableFilters().map((filterDef) =>
              renderFilterField(filterDef)
            )}
        </Stack>
      </Collapse>
    </Stack>
  );
};

const CompensationDataSource = ({ form, name, ...props }) => {
  const dataSources = _.get(form.values, name) ?? [];

  const onAddDataSource = () => {
    const newDataSource = {
      name: "",
      collectionName: "",
      filters: {},
    };
    const updatedDataSources = [...dataSources, newDataSource];
    form.setFieldValue(name, updatedDataSources);
  };

  const onRemoveDataSource = (index) => {
    const updatedDataSources = [...dataSources];
    updatedDataSources.splice(index, 1);
    form.setFieldValue(name, updatedDataSources);
  };

  const updateDataSource = (index, updatedDataSource) => {
    const updatedDataSources = [...dataSources];
    updatedDataSources[index] = updatedDataSource;
    form.setFieldValue(name, updatedDataSources);
  };

  return (
    <>
      <Stack spacing="md" mb="xl">
        <Accordion multiple>
          {dataSources.map((dataSource, index) => (
            <Accordion.Item key={index} value={`datasource-${index}`} mb="md">
              <Accordion.Control>
                <Group>
                  <IconDatabase size={16} />
                  <Text weight={500}>
                    {dataSource.name || `Data Source ${index + 1}`}
                  </Text>
                  <Text size="sm" color="dimmed">
                    {dataSource.collectionName || "No collection selected"}
                  </Text>
                  {dataSource.filters &&
                    Object.keys(dataSource.filters).length > 0 && (
                      <Text size="xs" color="blue">
                        Filtered
                      </Text>
                    )}
                  <div style={{ marginLeft: "auto" }}>
                    <ActionIcon
                      variant="filled"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveDataSource(index);
                      }}
                    >
                      <IconTrash size={13} />
                    </ActionIcon>
                  </div>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Paper withBorder p="sm">
                  <DataSourceForm
                    dataSource={dataSource}
                    onUpdate={updateDataSource}
                    index={index}
                  />
                </Paper>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>

      <Button
        size="xs"
        onClick={onAddDataSource}
        leftIcon={<IconPlus size={16} />}
      >
        Add Data Source
      </Button>
    </>
  );
};

export default CompensationDataSource;
